import React, { useRef } from 'react';
import { cn } from '@/lib/utils';
import { FOCUS_ACCENT } from './hooks';

interface FocusDialProps {
  /** Share of the dial that is lit, 0 to 1. */
  value: number;
  size: number;
  /**
   * 'ticks': sixty minute ticks, the lit ones are the time left and the one
   * at the leading end fades as its minute runs out. 'ring': a thin arc, for
   * glyph sizes where sixty ticks would blur together.
   */
  variant?: 'ticks' | 'ring';
  stroke?: number;
  ringing?: boolean;
  muted?: boolean;
  /** Draw in white, for a dial sitting on a dark or accent surface. */
  inverted?: boolean;
  /** Keep the accent but draw the track for a dark surface. */
  onDark?: boolean;
  /** Drag round the ticks to set whole minutes, 1 to 60. */
  onSetMinutes?: (minutes: number) => void;
  className?: string;
  children?: React.ReactNode;
}

const TICKS = 60;

/** Minute under the pointer: 0 at the top, clockwise, as 1..60. */
function minuteAt(e: React.PointerEvent, el: Element, prev: number): number {
  const rect = el.getBoundingClientRect();
  const x = e.clientX - rect.left - rect.width / 2;
  const y = e.clientY - rect.top - rect.height / 2;
  const deg = (Math.atan2(x, -y) * 180) / Math.PI;
  let m = Math.round(((deg + 360) % 360) / 6);
  if (m === 0) m = prev > 30 ? 60 : 1;
  // Crossing the seam at the top in one move: hold at the end you came from.
  if (Math.abs(m - prev) > 30) m = prev > 30 ? 60 : 1;
  return m;
}

export function FocusDial({
  value,
  size,
  variant = 'ticks',
  stroke = 3,
  ringing,
  muted,
  inverted,
  onDark,
  onSetMinutes,
  className,
  children,
}: FocusDialProps) {
  const filled = Math.min(1, Math.max(0, value));
  const accent = inverted ? '#fff' : muted ? 'hsl(var(--muted-foreground))' : FOCUS_ACCENT;
  const dragRef = useRef<number | null>(null);

  const pointer = onSetMinutes
    ? {
        onPointerDown: (e: React.PointerEvent<SVGSVGElement>) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          const m = minuteAt(e, e.currentTarget, Math.round(filled * TICKS) || 1);
          dragRef.current = m;
          onSetMinutes(m);
        },
        onPointerMove: (e: React.PointerEvent<SVGSVGElement>) => {
          if (dragRef.current === null) return;
          const m = minuteAt(e, e.currentTarget, dragRef.current);
          if (m !== dragRef.current) {
            dragRef.current = m;
            onSetMinutes(m);
          }
        },
        onPointerUp: () => {
          dragRef.current = null;
        },
        onPointerCancel: () => {
          dragRef.current = null;
        },
      }
    : {};

  const c = size / 2;

  return (
    <div className={cn('relative shrink-0', className)} style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={cn('block -rotate-90', onSetMinutes && 'cursor-grab touch-none active:cursor-grabbing')}
        aria-hidden
        {...pointer}
      >
        {variant === 'ticks' ? (
          Array.from({ length: TICKS }, (_, i) => {
            const run = filled * TICKS;
            const lit = i < Math.floor(run) ? 1 : i === Math.floor(run) ? run % 1 : 0;
            const leading = lit > 0 && i === Math.min(TICKS - 1, Math.ceil(run) - 1);
            const major = i % 5 === 0;
            const angle = (i / TICKS) * 2 * Math.PI;
            const outer = c - 2;
            const inner = outer - (major ? size * 0.075 : size * 0.055);
            return (
              <g key={i}>
                <line
                  x1={c + Math.cos(angle) * inner}
                  y1={c + Math.sin(angle) * inner}
                  x2={c + Math.cos(angle) * outer}
                  y2={c + Math.sin(angle) * outer}
                  stroke="currentColor"
                  strokeWidth={major ? 2.25 : 1.75}
                  strokeLinecap="round"
                  className={inverted ? 'text-white/20' : 'text-foreground/[0.12]'}
                />
                {lit > 0 && (
                  <line
                    x1={c + Math.cos(angle) * inner}
                    y1={c + Math.sin(angle) * inner}
                    x2={c + Math.cos(angle) * outer}
                    y2={c + Math.sin(angle) * outer}
                    stroke={ringing || leading ? accent : 'currentColor'}
                    strokeWidth={major ? 2.25 : 1.75}
                    strokeLinecap="round"
                    className={cn(
                      inverted ? 'text-white' : 'text-foreground',
                      'transition-opacity duration-700',
                      ringing && 'animate-pulse',
                    )}
                    style={{ opacity: lit }}
                  />
                )}
              </g>
            );
          })
        ) : (
          <>
            <circle
              cx={c}
              cy={c}
              r={c - stroke / 2}
              fill="none"
              stroke="currentColor"
              strokeWidth={stroke}
              className={inverted || onDark ? 'text-white/20' : 'text-foreground/10'}
            />
            <circle
              cx={c}
              cy={c}
              r={c - stroke / 2}
              fill="none"
              stroke={accent}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * (c - stroke / 2)}
              strokeDashoffset={2 * Math.PI * (c - stroke / 2) * (1 - filled)}
              className={cn('transition-[stroke-dashoffset] duration-1000 ease-linear', ringing && 'animate-pulse')}
              style={{ opacity: filled === 0 ? 0 : 1 }}
            />
          </>
        )}
      </svg>
      {children && (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center [&_button]:pointer-events-auto [&_input]:pointer-events-auto [&_label]:pointer-events-auto">
          {children}
        </div>
      )}
    </div>
  );
}
